
import React, { useState, useEffect } from 'react';
import { DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useInsightCards } from '@/hooks/admin/cards/useInsightCards';
import { useCivicEducationContent } from '@/hooks/admin/civic-education/useCivicEducationContent';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Search, LinkIcon } from 'lucide-react';
import { useCivicEducationRelationships } from '@/hooks/admin/civic-education/useCivicEducationRelationships';
import { ScrollArea } from '@/components/ui/scroll-area';

interface RelationshipManagerProps {
  itemId?: string;
  onClose?: () => void;
  linkedCards?: any[];
}

const RelationshipManager: React.FC<RelationshipManagerProps> = ({ 
  itemId,
  onClose,
  linkedCards = []
}) => {
  const { allCards, isLoadingCards } = useInsightCards();
  const { allContent, isLoadingContent } = useCivicEducationContent();
  const { saveRelationships, isLoading: isSaving } = useCivicEducationRelationships();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCardIds, setSelectedCardIds] = useState<string[]>([]);
  const [allRelationships, setAllRelationships] = useState<any[]>(linkedCards);
  
  // Initialize selected cards from existing links if editing a specific item
  useEffect(() => {
    if (itemId && linkedCards.length > 0) {
      const linkedCardIds = linkedCards
        .filter(link => link.content_id === itemId)
        .map(link => link.card_id);
      setSelectedCardIds(linkedCardIds);
    }
  }, [itemId, linkedCards]);

  // Filter cards based on search query
  const filteredCards = allCards.filter(card => 
    card.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    card.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
    card.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Handle checkbox toggling
  const handleToggleCard = (cardId: string) => {
    setSelectedCardIds(prev => {
      if (prev.includes(cardId)) {
        return prev.filter(id => id !== cardId);
      } else {
        return [...prev, cardId];
      }
    });
  };

  // Save relationships
  const handleSave = async () => {
    if (!itemId) return;
    
    try {
      await saveRelationships(itemId, selectedCardIds);
      if (onClose) onClose();
    } catch (error) {
      console.error('Failed to save relationships:', error);
    }
  };

  // Render content display when viewing all relationships
  const renderContentDisplay = () => {
    if (isLoadingContent) {
      return <div>Loading content...</div>;
    }

    if (allContent.length === 0) {
      return <div>No educational content available</div>;
    }

    // Group relationships by content
    const relationshipsByContent = allContent.map(content => {
      const contentLinks = allRelationships.filter(rel => rel.content_id === content.id);
      const linkedCardIds = contentLinks.map(link => link.card_id);
      const linkedCardCount = linkedCardIds.length;
      
      return {
        content,
        linkedCardCount,
        linkedCardIds
      };
    });

    return (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Title</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Linked Cards</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {relationshipsByContent.map(({ content, linkedCardCount }) => (
            <TableRow key={content.id}>
              <TableCell>{content.title}</TableCell>
              <TableCell className="capitalize">{content.type}</TableCell>
              <TableCell>{linkedCardCount} cards</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  // Render card selector when editing a specific content item's relationships
  const renderCardSelector = () => {
    if (isLoadingCards) {
      return <div>Loading cards...</div>;
    }

    if (filteredCards.length === 0) {
      return <div>No matching cards found</div>;
    }

    return (
      <>
        <div className="flex items-center space-x-2 mb-4">
          <Search className="h-4 w-4 text-gray-500" />
          <Input
            placeholder="Search cards by title, category, or description"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1"
          />
        </div>
        
        <ScrollArea className="h-[300px]">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">Link</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Category</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredCards.map((card) => (
                <TableRow key={card.id}>
                  <TableCell>
                    <Checkbox
                      checked={selectedCardIds.includes(card.id)}
                      onCheckedChange={() => handleToggleCard(card.id)}
                    />
                  </TableCell>
                  <TableCell>{card.title}</TableCell>
                  <TableCell>{card.category}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ScrollArea>
        
        <div className="mt-4 text-sm text-gray-500">
          {selectedCardIds.length} card{selectedCardIds.length !== 1 ? 's' : ''} selected
        </div>
      </>
    );
  };

  // If in dialog mode (editing specific item)
  if (itemId) {
    return (
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <LinkIcon className="h-5 w-5 mr-2" />
            Link Content to Insight Cards
          </DialogTitle>
        </DialogHeader>
        
        {renderCardSelector()}
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save Relationships'}
          </Button>
        </DialogFooter>
      </DialogContent>
    );
  }

  // If in tab mode (viewing all relationships)
  return renderContentDisplay();
};

export default RelationshipManager;
